import { apiIntegrations } from "@/api/integrations/integration.api";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  useToasts,
  Button,
  Skeleton,
  Stack,
  Switch,
  TabItem,
  Tabs,
  Text,
  TextTruncated,
  Title,
} from "@skailer-com/uikit";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { SettingsIntegrationEmployees } from "@/pages/settings/integrations/components/SettingsIntegrationEmployees.tsx";
import { SettingsIntegrationEmployeesActions } from "@/pages/settings/integrations/components/SettingsIntegrationEmployeesActions.tsx";
import { SettingsIntegrationNotFoundBanner } from "@/pages/settings/integrations/components/SettingsIntegrationNotFoundBanner.tsx";
import { SettingsIntegrationTabs } from "@/pages/settings/integrations/settings-integration.types.ts";
import {
  useIntegrationDisconnect,
  useIntegrationSlack,
  useIntegrationSlackUpdate,
} from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiIntegrationSlackSave,
  ApiIntegrationType,
  ApiSlackEmployee,
} from "@/types/integrations/integration.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { createColumnHelper } from "@tanstack/react-table";
import { parseInt } from "lodash";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { z } from "zod";

interface Props {
  tab: SettingsIntegrationTabs;
}

export const SettingsIntegrationSlack = ({ tab }: Props) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const disconnect = useIntegrationDisconnect();
  const { handleErrors } = useErrorHandle();

  useTitle(
    t("integrations:slack_title"),
    t("integrations:settings_integrations_title"),
    t("common:settings")
  );

  const { data: integration, isLoading } = useIntegrationSlack(id ? parseInt(id, 10) : undefined);

  const isEmployeesTab = tab === SettingsIntegrationTabs.employees;

  const handleTabChanged = (tabIndex: number) => {
    const urls = [
      `/settings/integrations/${id}/slack`,
      `/settings/integrations/${id}/slack/employees`,
    ];
    navigate(urls[tabIndex]);
  };

  const accountColumns = useMemo(() => {
    const columnHelper = createColumnHelper<ApiSlackEmployee>();
    return [
      columnHelper.display({
        id: "slack_email",
        header: t("integrations:slack_email"),
        cell: (cell) => <TextTruncated title={cell.row.original.slack_email ?? "-"} />,
        ...ColumnSizes.standard,
      }),
      columnHelper.display({
        id: "slack_name",
        header: t("integrations:slack_name"),
        cell: (cell) => <Text>{cell.row.original.slack_name ?? "-"}</Text>,
        ...ColumnSizes.standard,
      }),
    ];
  }, [t]);

  const SettingsIntegrationSlackSchemaObject = z.object({
    is_active: z.boolean(),
    sync_photo: z.boolean(),
    sync_name: z.boolean(),
    sync_hired_on: z.boolean(),
    sync_address: z.boolean(),
    sync_position: z.boolean(),
    sync_position_division: z.boolean(),
  });

  type SettingsIntegrationSlackSchema = z.infer<typeof SettingsIntegrationSlackSchemaObject>;
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SettingsIntegrationSlackSchema>({
    mode: "onSubmit",
    resolver: zodResolver(SettingsIntegrationSlackSchemaObject),
    values: {
      is_active: integration?.is_active ?? false,
      sync_photo: integration?.sync_photo ?? false,
      sync_name: integration?.sync_name ?? false,
      sync_hired_on: integration?.sync_hired_on ?? false,
      sync_address: integration?.sync_address ?? false,
      sync_position: integration?.sync_position ?? false,
      sync_position_division: integration?.sync_position_division ?? false,
    },
  });

  const isActive = watch("is_active");

  const manageIntegration = useIntegrationSlackUpdate(control);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.integrations.manageViewed, {
      [analyticProperties.source]: "Slack",
    });
  });

  const onSubmit = handleSubmit(async (schema) => {
    if (!integration) return;
    const payload: ApiIntegrationSlackSave = {
      is_active: schema.is_active,
      sync_photo: schema.sync_photo,
      sync_name: schema.sync_name,
      sync_hired_on: schema.sync_hired_on,
      sync_address: schema.sync_address,
      sync_position: schema.sync_position,
      sync_position_division: schema.sync_position_division,
    };

    try {
      await manageIntegration.mutateAsync({ id: integration.id, payload });
      analyticsService.trackEvent(analyticEvents.integrations.edited, {
        [analyticProperties.source]: "Slack",
      });

      showInfo({ title: t("common:you_changes_were_saved") });
    } catch {
      return;
    }
  });

  const handleDelete = async () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("integrations:disconnect_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        if (!integration) return;
        const result = await disconnect.mutateAsync(integration.id);

        if (result?.success) {
          analyticsService.trackEvent(analyticEvents.integrations.disconnected, {
            [analyticProperties.actionType]: "Slack",
          });
          navigate(`/settings/integrations`);
        }
      },
    });
  };

  const handleConnect = async () => {
    analyticsService.trackEvent(analyticEvents.integrations.connectStarted, {
      [analyticProperties.actionType]: "Slack",
    });

    const { data, error } = await apiIntegrations.install(ApiIntegrationType.slack);

    if (data) {
      window.location.href = data.authorization_url;
    } else handleErrors(error);
  };

  if (integration && !integration.has_credentials) {
    return (
      <Page>
        <Page.Header
          title={t("integrations:slack_title")}
          showBack={true}
          navigateTo={"/settings/integrations"}
        />
        <Page.Content>
          <Stack gap={"xl"} className={"container-sm mx-auto"}>
            <Title
              header={t("integrations:slack_not_connected")}
              caption={t("integrations:slack_not_connected_description")}
              paddingBottom
              paddingTop
            />
            <Stack gap={"sm"}>
              <DemoUnavailable className="w-full">
                <Button onClick={handleConnect} size={"lg"} className={"w-full"}>
                  {t("integrations:connect")}
                </Button>
              </DemoUnavailable>
            </Stack>
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  return (
    <Page className={isEmployeesTab ? "h-full" : undefined}>
      <Page.Header
        title={t("integrations:slack_title")}
        showBack={true}
        navigateTo={"/settings/integrations"}
      >
        {integration && (
          <SettingsIntegrationEmployeesActions
            integrationId={integration.id}
            integrationType={ApiIntegrationType.slack}
            isActive={integration.is_active}
            isEmployeesTab={isEmployeesTab}
          />
        )}
      </Page.Header>
      <Page.Content stretch={isEmployeesTab}>
        <Stack className={isEmployeesTab ? "px-16 flex-1 min-h-0 min-w-0" : "px-16"}>
          <Tabs
            fill={isEmployeesTab}
            onActiveTabChange={handleTabChanged}
            activeTab={isEmployeesTab ? 1 : 0}
          >
            <TabItem title={t("common:settings")}>
              {integration?.is_active && (
                <SettingsIntegrationNotFoundBanner
                  integrationId={integration.id}
                  integrationType={ApiIntegrationType.slack}
                  notFoundEmployeesCount={integration.not_found_employees_count}
                />
              )}
              <Stack gap={"xl"} className={"container-sm mx-auto"}>
                {isLoading && <Skeleton className={"w-full h-24"} />}
                <form onSubmit={onSubmit} className={"flex flex-col grow"}>
                  <Title header={t("integrations:manage")} paddingBottom paddingTop />
                  <Stack gap={"sm"}>
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          checked={value}
                          onChange={(newValue) => {
                            onChange(newValue);
                          }}
                          title={t("integrations:slack_active_switch")}
                          description={t("integrations:slack_active_switch_description")}
                        />
                      )}
                      control={control}
                      name={"is_active"}
                    />
                  </Stack>
                  {isActive && (
                    <>
                      <Stack gap={"md"} className={"pt-8"}>
                        {integration && !integration.is_all_scopes_installed && (
                          <Title
                            header={t("integrations:slack_reconnection_required_title")}
                            caption={t("integrations:slack_reconnection_required_description")}
                          />
                        )}

                        <DemoUnavailable>
                          <Button onClick={handleConnect}>{t("integrations:reconnect")}</Button>
                        </DemoUnavailable>
                      </Stack>

                      <Title header={t("integrations:sync_settings")} paddingBottom paddingTop />
                      <Title header={t("integrations:from_slack")} paddingBottom size={"xs"} />

                      <Controller
                        render={({ field: { value, onChange } }) => (
                          <Switch
                            checked={value}
                            onChange={(newValue) => {
                              onChange(newValue);
                            }}
                            size={"sm"}
                            title={t("integrations:slack_sync_photo_title")}
                            description={t("integrations:slack_sync_photo_description")}
                          />
                        )}
                        control={control}
                        name={"sync_photo"}
                      />
                      <Title
                        header={t("integrations:to_slack")}
                        paddingBottom
                        paddingTop
                        size={"xs"}
                      />
                      <Stack gap={"md"}>
                        <Controller
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={value}
                              onChange={(newValue) => {
                                onChange(newValue);
                              }}
                              size={"sm"}
                              title={t("integrations:slack_sync_name_title")}
                              description={t("integrations:slack_sync_name_description")}
                            />
                          )}
                          control={control}
                          name={"sync_name"}
                        />

                        <Controller
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={value}
                              onChange={(newValue) => {
                                onChange(newValue);
                              }}
                              size={"sm"}
                              title={t("integrations:slack_sync_hired_on_title")}
                              description={t("integrations:slack_sync_hired_on_description")}
                            />
                          )}
                          control={control}
                          name={"sync_hired_on"}
                        />

                        <Controller
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={value}
                              onChange={(newValue) => {
                                onChange(newValue);
                              }}
                              size={"sm"}
                              title={t("integrations:slack_sync_address_title")}
                              description={t("integrations:slack_sync_address_description")}
                            />
                          )}
                          control={control}
                          name={"sync_address"}
                        />

                        <Controller
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={value}
                              onChange={(newValue) => {
                                onChange(newValue);
                              }}
                              size={"sm"}
                              title={t("integrations:slack_sync_position_title")}
                              description={t("integrations:slack_sync_position_description")}
                            />
                          )}
                          control={control}
                          name={"sync_position"}
                        />
                        <Controller
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={value}
                              onChange={(newValue) => {
                                onChange(newValue);
                              }}
                              size={"sm"}
                              title={t("integrations:slack_sync_position_division_title")}
                              description={t(
                                "integrations:slack_sync_position_division_description"
                              )}
                            />
                          )}
                          control={control}
                          name={"sync_position_division"}
                        />
                      </Stack>
                    </>
                  )}
                  <ServerErrorField errors={errors} />
                </form>
                <DeleteListItem
                  onDelete={handleDelete}
                  title={t("integrations:disconnect") + " Slack"}
                  caption={integration?.identity}
                  deleteTitle={t("integrations:disconnect")}
                />

                <Button
                  onClick={onSubmit}
                  isLoading={manageIntegration.isPending}
                  size={"lg"}
                  className={"w-full"}
                  type={"submit"}
                >
                  {t("common:save")}
                </Button>
              </Stack>
            </TabItem>
            <TabItem title={t("core:employees")} className={"flex min-h-0 min-w-0 flex-1 flex-col"}>
              {integration && (
                <SettingsIntegrationEmployees
                  integrationId={integration.id}
                  integrationType={ApiIntegrationType.slack}
                  accountColumns={accountColumns}
                />
              )}
            </TabItem>
          </Tabs>
        </Stack>
      </Page.Content>
    </Page>
  );
};
